import { BreadcrumbComponent, Button, Loader } from 'ccs-ui-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import AppCard from '../../../../common/components/UI/Card/Card';
import { IConnectorList, IConnectorServiceDesc } from '../../../models';
import { AppDispatch, useSelector } from '../../../store/AppStore';
import { getConnectorListRequest } from '../../../store/actions/sagaActions/connectors/ConnectorListSagaActions';
import { getConnectorServiceDescRequest } from '../../../store/actions/sagaActions/connectors/ConnectorServiceDescSagaAction';
import { ErrorWarning } from '../../../_components/error-warning/ErrorWarning';
import { handleServiceData } from '../connector-list/ConnectorList';
import ConnectorDetailsMockData from '../connector-mock-data/ConnectorDetailsMockData.json';
import { getConnectorImageDetails } from '../helpers/ConnectorLogoHelper';

import classes from './ConnectorDetail.module.scss';

type connectorListType = {
  title: string;
  para: string;
}

type connectorDetailType = {
  overview: {
    para: string;
  };
  useCase: {
    heading: string;
    list: connectorListType[]
  },
  image: string;
};

const ConnectorDetail = () => {
  const { t: translate } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useNavigate();

  const dispatch = useDispatch<AppDispatch>();

  const { connectorListDetails } = useSelector(
    (state) => state.ConnectorListReducer
  );

  const { isLoading, success, errorMessage, connectorServiceDesc } = useSelector(
    (state) => state.ConnectorServiceDescReducer
  );

  /**
   * @method serviceRequestBody
   * @description request body for api
   */
  const serviceRequestBody = {
    services: handleServiceData(connectorListDetails, id ?? '')?.services,
    status: 'active'
  };

  /**
   * @method selectedSubArray
   * @description Finds the selected subarray in the connectorListDetails array based on the connectorName.
   * @param {Array} connectorListDetails - The array of connector list details.
   * @param {string} id - The connectorName to search for.
   */
  const selectedSubArray = connectorListDetails.find(
    (subarray: IConnectorList) => subarray.connectorName === id
  );

  /**
   * @method navigateServiceData
   * @description this function helps you navigate to viewService component
   * @param value
   */
  const navigateServiceData = (value: string) => {
    const param = {
      searchText: value,
      status: 'Active',
      toDate: '',
      fromDate: ''
    };
    history(`/control-plane/services/viewServiceDetails/${value}`, { state: { index: 0, param, connectorFlag: true, path: '' } });
  };

  useEffect(() => {
    dispatch(getConnectorListRequest());
    dispatch(getConnectorServiceDescRequest(serviceRequestBody));
  }, []);

  return (
    <div className={classes.container}>
      <BreadcrumbComponent />
      <Loader show={isLoading} />
      {(selectedSubArray && success && id) && (
        <div className={classes.cards}>
          <AppCard className={classes.leftCard}>
            <div className={classes.headerCard} data-testid="header">
              <div>
                <img src={getConnectorImageDetails(handleServiceData(connectorListDetails, id))} alt="logo" />
              </div>
              <div className={classes.heading}>
                <div className={classes.title}>{handleServiceData(connectorListDetails, id)?.connectorName}</div>
                <div className={classes.description}>{handleServiceData(connectorListDetails, id)?.connectorDescription}</div>
              </div>
            </div>
            <hr />
            {ConnectorDetailsMockData[0].items.map((item: connectorDetailType) => (
              <>
                <div>
                  <div className={classes.overview}>{translate('connector_component.overview_lbl')}</div>
                  <div className={classes.detailedOverview}>
                    {item.overview.para}
                  </div>
                </div>
                <div>
                  <div className={classes.useCaseHead}>
                    {translate('connector_component.useCase_txt', {
                      replace: { connectorName: id }
                    })}
                  </div>
                  <ul className={classes.detailedOverview}>
                    <div className={classes['use-case-heading']}>{item.useCase.heading}</div>
                    {item.useCase.list.map((listItem: connectorListType) => (
                      <li key={listItem.title}>
                        <span>
                          {listItem.title}
                        </span>
                        {listItem.para}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ))}
            <div className={classes['connector-api']}>
              {translate('connector_component.connector_apis', {
                replace: { connectorName: id }
              })}
              <div className={classes['api-list']}>
                <ul>
                  {connectorServiceDesc.map((val: IConnectorServiceDesc) => (
                    <li key={val.uuid}>
                      <Button variant="tertiary" onClick={() => navigateServiceData(val.serviceName)} data-testid="serviceName">
                        {val.serviceName}:
                      </Button>
                      {val.serviceDescription}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <hr />
          </AppCard>
        </div>
      )}
      {(!isLoading && errorMessage) && <ErrorWarning />}
      {(success && !selectedSubArray) && (
        <p className={classes['no-record-label']}>{translate('connector_component.no_connector_detail')}</p>
      )}
    </div>
  );
};
export default ConnectorDetail;
